import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, Code, Download, Copy, CheckCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { transferAPI } from '../api/api';
import ExportModal from '../components/ExportModal';

const TransactionDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    loadTransaction();
  }, [id]);

  const loadTransaction = async () => {
    try {
      const response = await transferAPI.getTransaction(id);
      setTransaction(response.data);
    } catch (error) {
      console.error('Error loading transaction:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <Eye className="w-12 h-12 text-terminal-green" />
        </motion.div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-terminal-green">Transaction not found</p>
      </div>
    );
  }

  const { transaction: txn, codes, from_account } = transaction;

  return (
    <div className="min-h-screen bg-black text-terminal-green p-4 md:p-8">
      <div className="scanline" />
      
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/transactions')}
            className="flex items-center gap-2 text-terminal-green-dim hover:text-terminal-green mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>BACK TO HISTORY</span>
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold cyber-font glow-text mb-2">
            TRANSACTION DETAILS
          </h1>
          <div className="flex items-center justify-between">
            <p className="text-terminal-green-dim text-sm font-mono">
              {txn.transaction_reference}
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowExportModal(true)}
              className="terminal-button px-4 py-2 flex items-center gap-2 text-sm"
            >
              <Download className="w-4 h-4" />
              <span>EXPORT</span>
            </motion.button>
          </div>
        </motion.div>

        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="terminal-card p-6"
          >
            <div className="flex items-center gap-2 mb-4 border-b border-terminal-green pb-3">
              <Eye className="w-5 h-5" />
              <h2 className="text-xl font-bold cyber-font">TRANSACTION INFORMATION</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoField label="Reference" value={txn.transaction_reference} copyable />
              <InfoField label="Status" value={txn.status} />
              <InfoField label="Transfer Type" value={txn.transfer_type} />
              <InfoField 
                label="Amount" 
                value={`$${txn.amount.toLocaleString()} ${txn.currency}`} 
              />
              <InfoField label="Recipient" value={txn.recipient_info} />
              <InfoField 
                label="Created At" 
                value={new Date(txn.created_at).toLocaleString()} 
              />
              {txn.completed_at && (
                <InfoField 
                  label="Completed At" 
                  value={new Date(txn.completed_at).toLocaleString()} 
                />
              )}
              {txn.status_message && (
                <InfoField 
                  label="Status Message" 
                  value={txn.status_message}
                  className="md:col-span-2" 
                />
              )}
            </div>
          </motion.div>

          {from_account && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="terminal-card p-6"
            >
              <div className="flex items-center gap-2 mb-4 border-b border-terminal-green pb-3">
                <Code className="w-5 h-5" />
                <h2 className="text-xl font-bold cyber-font">SOURCE ACCOUNT</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField label="Account Number" value={from_account.account_number} />
                <InfoField label="IBAN" value={from_account.iban || 'N/A'} />
                <InfoField label="Bank Name" value={from_account.bank_name || 'N/A'} />
                <InfoField label="SWIFT Code" value={from_account.swift_code || 'N/A'} />
              </div>
            </motion.div>
          )}

          {codes && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="terminal-card p-6"
            >
              <div className="flex items-center justify-between mb-4 border-b border-terminal-green pb-3">
                <div className="flex items-center gap-2">
                  <Code className="w-5 h-5" />
                  <h2 className="text-xl font-bold cyber-font">TRANSACTION CODES</h2>
                </div>
                <button className="text-xs px-3 py-1 border border-terminal-green hover:bg-terminal-green/20">
                  <Download className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {codes.transaction_code && <CodeField label="Transaction Code" value={codes.transaction_code} copyToClipboard={copyToClipboard} copiedField={copiedField} />}
                {codes.transfer_code && <CodeField label="Transfer Code" value={codes.transfer_code} copyToClipboard={copyToClipboard} copiedField={copiedField} />}
                {codes.access_code && <CodeField label="Access Code" value={codes.access_code} copyToClipboard={copyToClipboard} copiedField={copiedField} />}
                {codes.activation_code && <CodeField label="Activation Code" value={codes.activation_code} copyToClipboard={copyToClipboard} copiedField={copiedField} />}
                {codes.download_code && <CodeField label="Download Code" value={codes.download_code} copyToClipboard={copyToClipboard} copiedField={copiedField} />}
                {codes.receiving_access_code && <CodeField label="Receiving Access Code" value={codes.receiving_access_code} copyToClipboard={copyToClipboard} copiedField={copiedField} />}
                {codes.imad_number && <CodeField label="IMAD Number" value={codes.imad_number} copyToClipboard={copyToClipboard} copiedField={copiedField} />}
                {codes.utr && <CodeField label="UTR" value={codes.utr} copyToClipboard={copyToClipboard} copiedField={copiedField} />}
                {codes.sort_code && <CodeField label="Sort Code" value={codes.sort_code} copyToClipboard={copyToClipboard} copiedField={copiedField} />}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          transaction={txn}
          codes={codes}
          account={from_account}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
};

const InfoField = ({ label, value, copyable, className = '' }) => (
  <div className={className}>
    <div className="text-xs text-terminal-green-dim mb-1">{label}</div>
    <div className="text-sm font-mono break-all">{value}</div>
  </div>
);

const CodeField = ({ label, value, copyToClipboard, copiedField }) => (
  <div className="flex items-center justify-between p-3 border border-terminal-green/30 hover:border-terminal-green/60 transition-all">
    <div className="flex-1">
      <div className="text-xs text-terminal-green-dim mb-1">{label}</div>
      <div className="text-sm font-mono">{value}</div>
    </div>
    <button
      onClick={() => copyToClipboard(value, label)}
      className="ml-4 text-terminal-green-dim hover:text-terminal-green transition-all"
    >
      {copiedField === label ? (
        <CheckCircle className="w-5 h-5" />
      ) : (
        <Copy className="w-5 h-5" />
      )}
    </button>
  </div>
);

export default TransactionDetailPage;
